// Trade OS 新手引导
// 设计目标:
//   - 用户首次登录看到（用 localStorage 标记是否完成）
//   - 6 个核心能力一目了然
//   - 每个能力: 视频/截图占位 + 一句话说明 + "立即试用"按钮
//   - 完成后隐藏入口（可从设置重新打开）

import { Link, createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon';
import { Button, Card } from '../components/ui';

export const Route = createFileRoute('/onboarding')({
  component: Onboarding,
});

const STORAGE_KEY = 'tradeos.onboarding.done';

const FEATURES = [
  {
    icon: 'inbox',
    title: 'AI 教练',
    desc: '问任何外贸问题：Incoterm / 防骗 / 报价策略 / 单证填写',
    cta: '试试问问题',
    ctaHref: '/coach',
    example: '"FOB 和 CIF 在付款条件上有什么区别？"',
  },
  {
    icon: 'search',
    title: '客户背调',
    desc: '点客户详情 → 一键背调。AI 自动查 5 维风险（公司注册、邮箱后缀、国家风险、业务匹配、综合）',
    cta: '查看客户',
    ctaHref: '/customers',
    example: '🟢 A 级 80+ / 🟡 B 级 60-79 / 🔴 C 级 < 60',
  },
  {
    icon: 'sparkles',
    title: '询盘 AI 起草回复',
    desc: '收到询盘 → AI 自动生成多语言草稿 → 你审改 → 一键发送',
    cta: '查看询盘',
    ctaHref: '/inquiries',
    example: 'AI 懂你公司的历史报价、产品参数、客户偏好',
  },
  {
    icon: 'mail',
    title: '节日邮件',
    desc: '客户生日 + 6 大节日（春节/中秋/圣诞等）× 3 语言 = 18 模板自动发',
    cta: '查看节日',
    ctaHref: '/holidays',
    example: '零付费 Resend API，每天 9:00 自动跑',
  },
  {
    icon: 'camera',
    title: 'OCR 名片识别',
    desc: '展会拍 50 张名片 → AI 自动提取公司/邮箱/电话 → 一键入库',
    cta: '上传名片',
    ctaHref: '/ocr',
    example: 'CF Workers AI 免费 10K 张/天',
  },
  {
    icon: 'bot',
    title: 'MCP 多数据源拓客',
    desc: 'WHOIS + UK 公司注册 + 制裁名单 + 法院记录 + 新闻意图（5 个免费数据源）',
    cta: '了解 MCP',
    ctaHref: '/sourcing',
    example: 'v1.34+ 功能，飞书机器人已支持 MCP背调 指令',
  },
];

function Onboarding() {
  const [done, setDone] = useState(false);

  useEffect(() => {
    setDone(localStorage.getItem(STORAGE_KEY) === 'true');
  }, []);

  const handleDone = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setDone(true);
  };

  const handleReset = () => {
    localStorage.removeItem(STORAGE_KEY);
    setDone(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink">
            {done ? '🎉 引导完成' : '👋 欢迎使用 Trade OS'}
          </h1>
          <p className="text-sm text-ink-3 mt-1">
            {done
              ? '你已了解核心功能。随时可以从这里再复习。'
              : '6 个核心能力，让你一个人运营展会回来当天就能成交。'}
          </p>
        </div>
        {done && (
          <Button variant="ghost" size="sm" onClick={handleReset}>
            重新显示引导
          </Button>
        )}
      </div>

      {/* Feature cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {FEATURES.map((f) => (
          <Card key={f.title} className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-9 h-9 rounded-lg bg-accent/10 text-accent flex items-center justify-center">
                <Icon name={f.icon} size="md" aria-hidden />
              </div>
              <h3 className="font-semibold text-ink">{f.title}</h3>
            </div>
            <p className="text-sm text-ink-2 leading-relaxed flex-1">{f.desc}</p>
            {f.example && (
              <p className="text-xs text-ink-3 mt-2 italic font-mono bg-surface-2 px-2 py-1 rounded">
                {f.example}
              </p>
            )}
            <div className="mt-3">
              <Link to={f.ctaHref}>
                <Button variant="secondary" size="sm" block>
                  {f.cta} →
                </Button>
              </Link>
            </div>
          </Card>
        ))}
      </div>

      {/* Footer actions */}
      {!done && (
        <Card className="bg-accent/5 border-accent/20">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-ink">看完所有功能？</h3>
              <p className="text-sm text-ink-3 mt-1">点击"我已了解"后，下次启动不再自动显示。</p>
            </div>
            <Button variant="primary" onClick={handleDone}>
              ✓ 我已了解
            </Button>
          </div>
        </Card>
      )}

      {/* Tip */}
      <Card variant="bordered" className="bg-surface-2">
        <div className="flex items-start gap-3">
          <Icon name="info" size="md" className="text-accent mt-0.5" />
          <div className="text-sm text-ink-2">
            <p className="font-medium text-ink mb-1">💡 进阶玩法</p>
            <p className="leading-relaxed">
              飞书里 @机器人 发{' '}
              <code className="bg-surface px-1.5 py-0.5 rounded text-xs">
                MCP背调: 公司名, 国家
              </code>
              ， 立刻拿到多数据源背调报告。所有 MCP 工具零付费（5 个免费数据源）。
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
